import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { FP } from 'components/financepro';
import { parseExcelPreview, downloadTemplateXlsx } from 'modules/importacion/excelPreview';

/** Bloques chicos para no amarrar hilos de gunicorn (igual que la importación de chasis). */
const LOTE_CHUNK = 25;

/** Columnas de la plantilla → campo del backend. Solo descripción, costo y precio son obligatorias. */
const COLUMNAS = [
  { titulo: 'Codigo', campo: 'codigo' },
  { titulo: 'Descripcion', campo: 'descripcion', requerida: true },
  { titulo: 'Categoria', campo: 'departamento' },
  { titulo: 'Tipo', campo: 'tipoproducto' },
  { titulo: 'Clase', campo: 'catproducto' },
  { titulo: 'Medida', campo: 'unidadmedida' },
  { titulo: 'Costo', campo: 'costo', requerida: true },
  { titulo: 'Precio mayor', campo: 'mayor' },
  { titulo: 'Precio', campo: 'precio', requerida: true },
  { titulo: 'Existencia inicial', campo: 'existenciainicial' },
  { titulo: 'Cant caja', campo: 'cantcaja' },
  { titulo: 'Cant minima', campo: 'cantminima' },
  { titulo: 'Cant maxima', campo: 'cantmaxima' },
  { titulo: 'Impuesto', campo: 'impuesto' },
  { titulo: 'Ubicacion', campo: 'ubicacioninventario' },
  { titulo: 'Observaciones', campo: 'observaciones' },
];

/** "Precio Mayor", "precio_mayor" y "Categoría" cuentan como la misma columna. */
const normalizarEncabezado = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const CAMPO_POR_ENCABEZADO = COLUMNAS.reduce((acc, c) => {
  acc[normalizarEncabezado(c.titulo)] = c.campo;
  return acc;
}, {});
// Sinónimos frecuentes en los Excel que mandan los clientes.
Object.assign(CAMPO_POR_ENCABEZADO, {
  nombre: 'descripcion',
  nombredelproducto: 'descripcion',
  departamento: 'departamento',
  preciopormayor: 'mayor',
  mayor: 'mayor',
  inicial: 'existenciainicial',
  existencia: 'existenciainicial',
  cantidad: 'existenciainicial',
  itbis: 'impuesto',
  codigodebarra: 'codigo',
  codigodebarras: 'codigo',
});

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const esNumero = (v) => {
  const t = texto(v).replace(/,/g, '');
  return t === '' || (!Number.isNaN(Number(t)) && Number(t) >= 0);
};

const ESTADO_CHIP = {
  pendiente: { label: 'Lista', bg: FP.color.accentBlueSoft, fg: FP.color.accentBlue },
  invalida: { label: 'Revisar', bg: FP.color.dangerSoft, fg: '#9F1239' },
  fallo: { label: 'No se guardó', bg: FP.color.dangerSoft, fg: '#9F1239' },
  yaexiste: { label: 'Ya existe', bg: FP.color.warningSoft, fg: '#92400E' },
};

const ImportarProductosExcel = ({
  open,
  onClose,
  onImportado,
  selectedCompany,
  usuarioLog,
  claveLog,
  localidades,
  localidadInicial,
  localidadFijada,
  departamentos,
  reglasImpuestos,
  showSuccess,
  showError,
}) => {
  const inputRef = useRef(null);
  const [localidad, setLocalidad] = useState('');
  const [deptoDefecto, setDeptoDefecto] = useState('');
  const [impuestoDefecto, setImpuestoDefecto] = useState('');
  const [archivo, setArchivo] = useState('');
  const [filas, setFilas] = useState([]);
  const [aviso, setAviso] = useState('');
  const [leyendo, setLeyendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [resumen, setResumen] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocalidad(localidadFijada?.texto || localidadInicial || '');
    setFilas([]);
    setArchivo('');
    setAviso('');
    setResumen(null);
    setProgreso(null);
  }, [open, localidadInicial, localidadFijada]);

  const deptosPorNombre = useMemo(() => {
    const m = new Set();
    (departamentos || []).forEach((d) => {
      const n = texto(d.nombre || d.texto).toLowerCase();
      if (n) m.add(n);
    });
    return m;
  }, [departamentos]);

  /** Validación rápida en el navegador; el backend vuelve a validar todo. */
  const validarFila = (datos, codigosRepetidos) => {
    const p = [];
    if (!texto(datos.descripcion)) p.push('falta la descripción');
    if (texto(datos.costo) === '') p.push('falta el costo');
    else if (!esNumero(datos.costo)) p.push('el costo no es un número');
    if (texto(datos.precio) === '') p.push('falta el precio');
    else if (!esNumero(datos.precio)) p.push('el precio no es un número');
    ['mayor', 'existenciainicial', 'cantcaja', 'cantminima', 'cantmaxima'].forEach((c) => {
      if (!esNumero(datos[c])) p.push(`${COLUMNAS.find((x) => x.campo === c).titulo.toLowerCase()} no es un número`);
    });
    const depto = texto(datos.departamento) || deptoDefecto;
    if (!depto) p.push('falta la categoría');
    else if (!deptosPorNombre.has(depto.toLowerCase())) p.push(`la categoría "${depto}" no existe`);
    if (!texto(datos.impuesto) && !impuestoDefecto) p.push('falta el impuesto');
    const codigo = texto(datos.codigo);
    if (codigo && codigosRepetidos.has(codigo)) p.push(`el código ${codigo} se repite en el archivo`);
    if (!p.length) return '';
    const m = p.join('; ');
    return m.charAt(0).toUpperCase() + m.slice(1);
  };

  // Revalida cuando cambian los valores por defecto del lote.
  const filasValidadas = useMemo(() => {
    const conteo = new Map();
    filas.forEach((f) => {
      const c = texto(f.datos.codigo);
      if (c) conteo.set(c, (conteo.get(c) || 0) + 1);
    });
    const repetidos = new Set([...conteo.entries()].filter(([, n]) => n > 1).map(([c]) => c));
    return filas.map((f) => {
      if (f.estado === 'yaexiste' || f.estado === 'fallo') return f;
      const motivo = validarFila(f.datos, repetidos);
      return { ...f, estado: motivo ? 'invalida' : 'pendiente', motivo };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filas, deptoDefecto, impuestoDefecto, deptosPorNombre]);

  const importables = filasValidadas.filter((f) => f.estado === 'pendiente' || f.estado === 'fallo');
  const conProblemas = filasValidadas.filter((f) => f.estado === 'invalida').length;

  const handleArchivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLeyendo(true);
    setAviso('');
    setResumen(null);
    try {
      const { headers, rows } = await parseExcelPreview(file, { maxPreviewRows: Infinity });
      const mapa = {};
      const ignoradas = [];
      headers.forEach((h) => {
        const campo = CAMPO_POR_ENCABEZADO[normalizarEncabezado(h)];
        if (campo && !Object.values(mapa).includes(campo)) mapa[h] = campo;
        else ignoradas.push(h);
      });
      const faltan = COLUMNAS.filter((c) => c.requerida && !Object.values(mapa).includes(c.campo));
      if (faltan.length) {
        setFilas([]);
        setArchivo('');
        showError(
          `Al archivo le faltan las columnas: ${faltan.map((c) => c.titulo).join(', ')}. Descargue la plantilla para ver el formato.`,
        );
        return;
      }
      const nuevas = rows.map((r, i) => {
        const datos = {};
        Object.entries(mapa).forEach(([h, campo]) => {
          datos[campo] = r[h];
        });
        return { fila: i + 2, datos, estado: 'pendiente', motivo: '' };
      });
      setFilas(nuevas);
      setArchivo(file.name);
      if (!nuevas.length) setAviso('El archivo no trae filas de productos debajo de los encabezados.');
      else if (ignoradas.length) setAviso(`Se ignoraron las columnas: ${ignoradas.join(', ')}.`);
    } catch (err) {
      showError(err.message || 'No se pudo leer el archivo');
    } finally {
      setLeyendo(false);
    }
  };

  const handleImportar = async () => {
    if (progreso || !importables.length) return;
    if (!localidad.includes('-')) {
      showError('Seleccione la localidad de los productos');
      return;
    }
    const servidor = process.env.REACT_APP_SERVIDOR || '';
    const pendientes = importables.map((f) => f.fila);
    const resultado = new Map(); // fila -> { estado, motivo } | 'guardada'
    let guardadas = 0;
    let corte = '';
    setProgreso({ done: 0, total: pendientes.length });
    try {
      for (let i = 0; i < pendientes.length; i += LOTE_CHUNK) {
        const bloque = pendientes.slice(i, i + LOTE_CHUNK);
        const productos = bloque.map((n) => filas.find((f) => f.fila === n).datos);
        const response = await fetch(servidor + '/api/productosbackend_importarlote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            localidad,
            departamento_defecto: deptoDefecto,
            impuesto_defecto: impuestoDefecto,
            productos,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.Error || 'No se pudo guardar el bloque de productos');
        (data.inserted || []).forEach((x) => {
          resultado.set(bloque[x.index], 'guardada');
          guardadas += 1;
        });
        (data.errors || []).forEach((x) => {
          resultado.set(bloque[x.index], { estado: x.yaexiste ? 'yaexiste' : 'fallo', motivo: x.message });
        });
        setProgreso({ done: Math.min(i + bloque.length, pendientes.length), total: pendientes.length });
      }
    } catch (err) {
      // Los bloques ya confirmados quedan guardados; el resto sigue en la grilla.
      corte = err.message || 'Se cortó la importación';
    }

    // Las guardadas salen de la grilla; las demás se quedan con su motivo.
    setFilas((prev) =>
      prev
        .filter((f) => resultado.get(f.fila) !== 'guardada')
        .map((f) => {
          const r = resultado.get(f.fila);
          return r && r !== 'guardada' ? { ...f, estado: r.estado, motivo: r.motivo } : f;
        }),
    );
    const fallidas = [...resultado.values()].filter((r) => r !== 'guardada');
    setResumen({
      guardadas,
      yaexisten: fallidas.filter((r) => r.estado === 'yaexiste').length,
      fallidas: fallidas.filter((r) => r.estado === 'fallo').length,
      corte,
    });
    setProgreso(null);
    if (guardadas > 0) {
      showSuccess(`Se importaron ${guardadas} productos`);
      if (onImportado) onImportado();
    } else if (corte) {
      showError(corte);
    }
  };

  const handleCerrar = () => {
    if (progreso) return;
    onClose();
  };

  const columnasVista = ['descripcion', 'codigo', 'departamento', 'costo', 'precio', 'existenciainicial', 'impuesto'];

  return (
    <Dialog
      open={open}
      onClose={handleCerrar}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.md } }}
    >
      <DialogTitle sx={{ px: 3, py: 2, borderBottom: `1px solid ${FP.color.border}`, bgcolor: '#FAFBFE' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
          Importar productos desde Excel
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: FP.color.textMuted, mt: 0.3 }}>
          La primera fila es de encabezados. Obligatorias: Descripcion, Costo y Precio. Sin código, se genera uno.
          Volver a subir el mismo archivo no duplica productos.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: '20px !important' }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={localidades || []}
              getOptionLabel={(o) => o.texto || ''}
              value={(localidades || []).find((l) => l.texto === localidad) || null}
              onChange={(e, v) => !localidadFijada && setLocalidad(v ? v.texto : '')}
              disabled={!!localidadFijada || !!progreso}
              disableClearable={!!localidadFijada}
              renderInput={(params) => <TextField {...params} label="Localidad" required size="small" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={departamentos || []}
              getOptionLabel={(o) => o.nombre || o.texto || ''}
              value={(departamentos || []).find((d) => (d.nombre || d.texto) === deptoDefecto) || null}
              onChange={(e, v) => setDeptoDefecto(v ? v.nombre || v.texto : '')}
              disabled={!!progreso}
              renderInput={(params) => (
                <TextField {...params} label="Categoría para filas sin categoría" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={!!progreso}>
              <InputLabel id="imp-defecto-label">Impuesto para filas sin impuesto</InputLabel>
              <Select
                labelId="imp-defecto-label"
                label="Impuesto para filas sin impuesto"
                value={impuestoDefecto}
                onChange={(e) => setImpuestoDefecto(e.target.value)}
              >
                <MenuItem value="">
                  <em>Ninguno</em>
                </MenuItem>
                {(reglasImpuestos || []).map((r) => (
                  <MenuItem key={r.id} value={`${r.nombre}-${r.porcentaje}`}>
                    {r.nombre}-{r.porcentaje}%
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadTemplateXlsx('plantilla_productos_pos.xlsx', COLUMNAS.map((c) => c.titulo))}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Descargar plantilla
          </Button>
          <Button
            variant="outlined"
            startIcon={leyendo ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={leyendo || !!progreso}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {archivo ? 'Cambiar archivo' : 'Cargar Excel'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleArchivo}
          />
          {archivo && (
            <Typography sx={{ fontSize: '0.85rem', color: FP.color.textMuted }}>
              {archivo}: {filasValidadas.length} filas, {importables.length} listas
              {conProblemas ? `, ${conProblemas} por revisar` : ''}
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontSize: '0.78rem', color: FP.color.textMuted, mb: 2 }}>
          Tipo: Producto o Servicio (vacío = Producto). Clase: Materia Prima o Producto Terminado. Medida: UDS, KGS o
          LBS. Impuesto: el nombre o el porcentaje configurado (p. ej. 18). Precio mayor vacío = el precio. La
          categoría debe existir en el catálogo.
        </Typography>

        {aviso && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {aviso}
          </Alert>
        )}
        {resumen && (
          <Alert severity={resumen.corte || resumen.fallidas ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Se guardaron {resumen.guardadas} productos.
            {resumen.yaexisten ? ` ${resumen.yaexisten} ya existían.` : ''}
            {resumen.fallidas ? ` ${resumen.fallidas} no se guardaron (vea el motivo en la tabla).` : ''}
            {resumen.corte ? ` ${resumen.corte}. Las filas que faltan siguen en la tabla para reintentar.` : ''}
          </Alert>
        )}
        {progreso && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={(progreso.done / Math.max(progreso.total, 1)) * 100} />
            <Typography sx={{ fontSize: '0.8rem', color: FP.color.textMuted, mt: 0.5 }}>
              Guardando {progreso.done} de {progreso.total}…
            </Typography>
          </Box>
        )}

        {filasValidadas.length > 0 && (
          <TableContainer sx={{ maxHeight: 420, border: `1px solid ${FP.color.border}`, borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Fila</TableCell>
                  <TableCell>Estado</TableCell>
                  {columnasVista.map((c) => (
                    <TableCell key={c}>{COLUMNAS.find((x) => x.campo === c).titulo}</TableCell>
                  ))}
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filasValidadas.map((f) => {
                  const chip = ESTADO_CHIP[f.estado] || ESTADO_CHIP.pendiente;
                  return (
                    <TableRow key={f.fila}>
                      <TableCell>{f.fila}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 700 }}
                        />
                      </TableCell>
                      {columnasVista.map((c) => (
                        <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}>
                          {c === 'departamento'
                            ? texto(f.datos[c]) || <em style={{ color: FP.color.textMuted }}>{deptoDefecto}</em>
                            : c === 'impuesto'
                              ? texto(f.datos[c]) || <em style={{ color: FP.color.textMuted }}>{impuestoDefecto}</em>
                              : texto(f.datos[c])}
                        </TableCell>
                      ))}
                      <TableCell sx={{ color: f.estado === 'yaexiste' ? '#92400E' : '#9F1239', minWidth: 220 }}>
                        {f.motivo}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
        <Button onClick={handleCerrar} disabled={!!progreso} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          onClick={handleImportar}
          disabled={!importables.length || !!progreso}
          startIcon={progreso ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          {progreso ? 'Importando…' : `Importar ${importables.length} productos`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportarProductosExcel;
